import { GOAL_2K_BOAT_CLASSES } from '../constants/boatClasses';
import { formatTwoKTime, parseTwoKTime } from '../utils/twoKTime';

export function emptyClassGoalState(twoKGoals = []) {
  const state = {};
  GOAL_2K_BOAT_CLASSES.forEach(({ value }) => {
    const row = (twoKGoals || []).find((goal) => goal.boat_class === value) || {};
    state[value] = {
      goalTime: formatTwoKTime(row.goal_2k_seconds),
      coachTime: formatTwoKTime(row.coach_goal_2k_seconds)
    };
  });
  return state;
}

export function classGoalsToPayload(classGoalState, canEditCoach) {
  return GOAL_2K_BOAT_CLASSES.map(({ value }) => {
    const row = classGoalState[value] || {};
    const payload = {
      boatClass: value,
      goal2kSeconds: parseTwoKTime(row.goalTime)
    };
    if (canEditCoach) {
      payload.coachGoal2kSeconds = parseTwoKTime(row.coachTime);
    }
    return payload;
  });
}

export function TwoKGoalsSummary({ twoKGoals = [], defaultSeconds, defaultCoachSeconds }) {
  const rows = GOAL_2K_BOAT_CLASSES.map(({ value, label }) => {
    const row = twoKGoals.find((goal) => goal.boat_class === value);
    const effective = row?.effective_goal_2k_seconds
      || row?.coach_goal_2k_seconds
      || row?.goal_2k_seconds;
    return {
      value,
      label,
      time: formatTwoKTime(effective),
      coach: row?.coach_goal_2k_seconds != null
    };
  }).filter((row) => row.time);

  const defaultTime = formatTwoKTime(defaultCoachSeconds || defaultSeconds);

  if (rows.length === 0 && !defaultTime) {
    return <span className="text-gray-400">Not set</span>;
  }

  return (
    <div className="space-y-1 text-sm">
      {defaultTime && (
        <div>
          <span className="text-gray-500">Default:</span>{' '}
          <span className="font-mono">{defaultTime}</span>
          {defaultCoachSeconds != null && (
            <span className="ml-2 text-xs text-indigo-700">coach override</span>
          )}
        </div>
      )}
      {rows.map((row) => (
        <div key={row.value}>
          <span className="text-gray-500">{row.label}:</span>{' '}
          <span className="font-mono">{row.time}</span>
          {row.coach && (
            <span className="ml-2 text-xs text-indigo-700">coach override</span>
          )}
        </div>
      ))}
    </div>
  );
}

export function TwoKGoalsEditor({
  classGoalState,
  onChange,
  canEditCoach = false,
  defaultGoalTime,
  defaultCoachTime,
  onDefaultGoalChange,
  onDefaultCoachChange
}) {
  const updateClass = (boatClass, field, value) => {
    onChange({
      ...classGoalState,
      [boatClass]: {
        ...(classGoalState[boatClass] || { goalTime: '', coachTime: '' }),
        [field]: value
      }
    });
  };

  return (
    <div className="md:col-span-2 space-y-3">
      <div>
        <label className="block font-semibold mb-1">2k Goals by boat class</label>
        <p className="text-xs text-gray-500 mb-2">
          Prognostic scores use the goal for the result&apos;s boat class (erg vs 1x vs 8+, etc.).
          Default is used only when that class has no goal.
        </p>
      </div>
      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="px-3 py-2 font-medium text-gray-600">Boat</th>
              <th className="px-3 py-2 font-medium text-gray-600">2k goal</th>
              {canEditCoach && (
                <th className="px-3 py-2 font-medium text-gray-600">Coach override</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            <tr className="bg-white">
              <td className="px-3 py-2 text-gray-700">Default</td>
              <td className="px-3 py-2">
                <input
                  type="text"
                  value={defaultGoalTime || ''}
                  onChange={(e) => onDefaultGoalChange(e.target.value)}
                  placeholder="7:00.0"
                  className="border p-2 rounded w-full text-theme-primary bg-white font-mono"
                />
              </td>
              {canEditCoach && (
                <td className="px-3 py-2">
                  <input
                    type="text"
                    value={defaultCoachTime || ''}
                    onChange={(e) => onDefaultCoachChange(e.target.value)}
                    placeholder="optional"
                    className="border p-2 rounded w-full text-theme-primary bg-white font-mono"
                  />
                </td>
              )}
            </tr>
            {GOAL_2K_BOAT_CLASSES.map(({ value, label }) => {
              const row = classGoalState[value] || { goalTime: '', coachTime: '' };
              return (
                <tr key={value} className="bg-white">
                  <td className="px-3 py-2 text-gray-700">{label}</td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={row.goalTime}
                      onChange={(e) => updateClass(value, 'goalTime', e.target.value)}
                      placeholder="M:SS.s"
                      className="border p-2 rounded w-full text-theme-primary bg-white font-mono"
                    />
                  </td>
                  {canEditCoach && (
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={row.coachTime}
                        onChange={(e) => updateClass(value, 'coachTime', e.target.value)}
                        placeholder="optional"
                        className="border p-2 rounded w-full text-theme-primary bg-white font-mono"
                      />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {canEditCoach && (
        <p className="text-xs text-gray-500">Leave a coach override blank to use the athlete&apos;s goal for that class.</p>
      )}
    </div>
  );
}
